import { useCallback, useEffect, useRef, useState } from 'react'
import type { BeurtStand, BeurtStatus } from '../../gedeeld/types'
import { c, Fout } from './api'
import { meldFout } from './staat'

/* ===========================================================================
 *  Een vraag die loopt, volgen
 *
 *  De AI draait op de pc op kantoor (LM Studio, eerder Ollama), via een wachtrij. Een antwoord
 *  duurt drie seconden of een minuut, en er komt geen stroom van woorden
 *  terug: de vraag gaat erin, en om de paar seconden vraagt het scherm hoe
 *  het ermee staat (de actie stand). Wat er dan terugkomt -- "er staan er
 *  twee vóór je", "zoekt in inkoopfacturen", "klaar, hier is het antwoord"
 *  -- is wat het scherm laat zien, want een draaiend rondje zonder uitleg is
 *  na twintig seconden een rondje waarvan iemand denkt dat het vastzit.
 *
 *  Het tempo: eerst vlot (een korte vraag is in een paar seconden klaar en
 *  dan wil je niet nog drie seconden wachten op de volgende keer vragen),
 *  daarna rustiger. Een vraag die al een minuut loopt wordt niet sneller
 *  van elke 1,2 seconde kijken; de server wel drukker.
 *
 *  Een netwerkfout halverwege is geen mislukte vraag. De pc rekent gewoon
 *  door; het scherm vraagt het straks nog een keer.
 * =========================================================================== */

const AF: ReadonlySet<BeurtStatus> = new Set(['klaar', 'mislukt', 'gestopt'])

export const isAf = (b: Pick<BeurtStand, 'status'>) => AF.has(b.status)

/** Hoe lang tussen twee keer vragen: 15 keer vlot, dan rustiger, dan rustig. */
const tussenpoos = (keer: number) => (keer < 15 ? 1200 : keer < 60 ? 2500 : 5000)

/** Fouten waarbij doorvragen zin heeft: geen verbinding, server even weg, te druk. */
const tijdelijk = (e: unknown) => !(e instanceof Fout) || e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500

export interface Beurt {
  /** De laatste stand van de vraag die loopt; null als er niets loopt. */
  stand: BeurtStand | null
  bezig: boolean
  /** Een vraag gaan volgen: net gesteld, of teruggevonden in een gesprek. */
  volg: (b: BeurtStand) => void
  /** De pc vragen te stoppen. */
  stop: () => Promise<void>
  /** Niets meer volgen, zonder de server iets te vragen (bijvoorbeeld bij een ander gesprek). */
  wis: () => void
  stoppen: boolean
}

export function useBeurt(opAf: (b: BeurtStand) => void): Beurt {
  const [stand, setStand] = useState<BeurtStand | null>(null)
  const [stoppen, setStoppen] = useState(false)
  const klok = useRef<ReturnType<typeof setTimeout> | null>(null)
  /* Welke vraag we volgen. Een antwoord op een oude vraag (na wis of een
     nieuwe volg) wordt zo herkend en weggegooid. */
  const volgt = useRef<string | null>(null)
  const laatste = useRef<BeurtStand | null>(null)
  const opAfRef = useRef(opAf)
  opAfRef.current = opAf

  const halt = () => {
    if (klok.current) clearTimeout(klok.current)
    klok.current = null
  }

  useEffect(() => () => { halt(); volgt.current = null }, [])

  const afronden = useCallback((b: BeurtStand) => {
    halt()
    volgt.current = null
    laatste.current = null
    setStand(null)
    setStoppen(false)
    opAfRef.current(b)
  }, [])

  const plan = useCallback((id: string, keer: number) => {
    halt()
    klok.current = setTimeout(async () => {
      if (volgt.current !== id) return
      try {
        const { beurt } = await c('stand', { beurtId: id })
        if (volgt.current !== id) return
        laatste.current = beurt
        if (isAf(beurt)) { afronden(beurt); return }
        setStand(beurt)
        plan(id, keer + 1)
      } catch (e) {
        if (volgt.current !== id) return
        if (e instanceof Fout && e.status === 401) { halt(); volgt.current = null; return }
        if (tijdelijk(e)) { plan(id, keer + 1); return }
        /* De server zegt dat deze vraag niet (meer) bestaat of niet van jou
           is: verder vragen heeft geen zin. Laat het in het gesprek zien. */
        const vorig = laatste.current
        afronden({
          id,
          gesprekId: vorig?.gesprekId ?? '',
          status: 'mislukt',
          stappen: vorig?.stappen ?? [],
          sinds: vorig?.sinds ?? Date.now(),
          fout: e instanceof Error ? e.message : String(e),
        })
      }
    }, tussenpoos(keer))
  }, [afronden])

  const volg = useCallback((b: BeurtStand) => {
    halt()
    volgt.current = b.id
    laatste.current = b
    setStoppen(false)
    if (isAf(b)) { afronden(b); return }
    setStand(b)
    plan(b.id, 0)
  }, [afronden, plan])

  const stop = useCallback(async () => {
    const id = volgt.current
    if (!id) return
    setStoppen(true)
    try {
      const { beurt } = await c('stop', { beurtId: id })
      if (volgt.current !== id) return
      if (isAf(beurt)) afronden(beurt)
      else setStand(beurt)
    } catch (e) {
      setStoppen(false)
      meldFout(e)
    }
  }, [afronden])

  const wis = useCallback(() => {
    halt()
    volgt.current = null
    laatste.current = null
    setStand(null)
    setStoppen(false)
  }, [])

  return { stand, bezig: stand !== null, volg, stop, wis, stoppen }
}

/**
 * De regel onder het denk-bolletje: wat er nu met je vraag gebeurt.
 * `waarschuwing` als het iets is waar je iets mee moet (de pc staat uit).
 */
export function standTekst(s: BeurtStand): { tekst: string; waarschuwing?: boolean } {
  switch (s.status) {
    case 'wacht':
      if (s.online === false) {
        return { tekst: 'De pc op kantoor lijkt uit te staan — je vraag blijft een uur in de wachtrij', waarschuwing: true }
      }
      if (s.voor && s.voor > 0) return { tekst: `In de wachtrij: ${s.voor} ${s.voor === 1 ? 'vraag' : 'vragen'} vóór je` }
      return { tekst: 'Doorgegeven aan de eigen AI…' }
    case 'denkt':
      return { tekst: 'Cowork denkt na…' }
    case 'tool':
    case 'verwerken': {
      const bezig = [...s.stappen].reverse().find((x) => x.status === 'bezig')
      return { tekst: bezig ? `${bezig.label.replace(/[.…\s]+$/, '')}…` : 'Gegevens ophalen…' }
    }
    case 'klaar': return { tekst: 'Klaar' }
    case 'mislukt': return { tekst: s.fout ?? 'Het lukte niet', waarschuwing: true }
    case 'gestopt': return { tekst: 'Gestopt' }
  }
}
